import api from "./axios";

var endpoint = "itinerary";

function stripClientOwnershipFields(form) {
  const payload = { ...form };
  delete payload.created_by;
  delete payload.updated_by;
  delete payload.org_id;
  delete payload.state;
  delete payload.expires_at;
  delete payload.state_updated_at;
  delete payload.updated_at;
  return payload;
}

/**
 * Itinerary API Service
 *
 * API Changes (Breaking):
 * - POST/PATCH: `itinerary_tour` renamed to `booking_items`
 * - POST/PATCH: Transportation fields moved to `custom_field_values` object
 * - POST/PATCH: `quantity_of_participants` moved from global to per-booking-item
 * - GET: `itinerary_tour` removed, data now in `slots_itineraries`
 * - GET: Each slot includes `custom_field_values` and service.service_type
 *
 * New Request Structure:
 * {
 *   customer_id: number,
 *   booking_items: [
 *     {
 *       service_id: number,
 *       service_slot_id: number | null,
 *       start_datetime: string (ISO),
 *       end_datetime: string (ISO),
 *       quantity_of_participants: number,
 *       custom_field_values: {
 *         [key: string]: string | number | boolean
 *       }
 *     }
 *   ]
 * }
 *
 * New Response Structure:
 * {
 *   id: number,
 *   code: string,
 *   slots_itineraries: [
 *     {
 *       id: number,
 *       service: {
 *         id: number,
 *         name: string,
 *         service_type: {
 *           id: number,
 *           name: string,
 *           custom_fields_schema: Array
 *         }
 *       },
 *       custom_field_values: { ... },
 *       subtotal: number,
 *       total_amount: number
 *     }
 *   ]
 * }
 */
const itineraryRequest = {
  /**
   * Get all itineraries for the active organization context.
   * Response includes slots_itineraries with custom_field_values
   * @returns {Promise} List of itineraries
   */
  getItineraries() {
    return api.get(`/${endpoint}`);
  },

  /**
   * Create new itinerary
   * @param {Object} form - Itinerary data with booking_items
   * @param {number} form.customer_id - Customer ID
   * @param {Array} form.booking_items - Array of booking items (each with quantity_of_participants)
   * @returns {Promise} Created itinerary with slots_itineraries
   */
  save(form) {
    const payload = stripClientOwnershipFields(form);
    delete payload.services_amount;
    delete payload.extra_charge_total;
    delete payload.code;
    return api.post(`/${endpoint}`, payload);
  },

  /**
 * Update existing itinerary - only allowed fields
 * @param {number} id - Itinerary ID
 * @param {Object} form - Only customer_id, tags, and booking_item_notes allowed
 * @returns {Promise} Updated itinerary with slots_itineraries
 */
  update(id, form) {
    const cleanForm = stripClientOwnershipFields(form);
    const payload = {};
    if (cleanForm.customer_id !== undefined)
      payload.customer_id = cleanForm.customer_id;
    if (cleanForm.tags !== undefined) payload.tags = cleanForm.tags;
    if (cleanForm.booking_item_notes !== undefined)
      payload.booking_item_notes = cleanForm.booking_item_notes;
    return api.patch(`/${endpoint}/${id}`, payload);
  },

  updateState(id, state) {
    return api.patch(`/${endpoint}/${id}/state`, { state });
  },

  /**
   * Delete itinerary
   * @param {number} id - Itinerary ID
   * @returns {Promise} Deletion confirmation
   */
  delete(id) {
    return api.delete(`/${endpoint}/${id}`);
  },
};

export default itineraryRequest;
